































































































































// Composition
import { ref, Ref, defineComponent, computed } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'

// Store
import { useCompareProductsStore } from '@/store/compare-products'

// Components
import Applications from '@/components/feature/search/results/common/Applications.vue'
import Reactivity from '@/components/feature/search/results/common/Reactivity.vue'
import ProductPanel from '@/components/feature/search/results/searchtype/ProductPanel.vue'
import AddToBasketIcon from '@/components/feature/search/results/searchtype/ProductAddToBasketIcon.vue'
import SignalStarIcon from '@/components/feature/signalstar/SignalStarIcon.vue'
import Skeleton from '@/components/global/skeletons/Skeleton.vue'

// Composables
import useSearch from '@/composables/search/useSearch'
import useSearchAnalytics from '@/composables/analytics/useSearchAnalytics'
import { isInBasket } from '@/composables/checkout/useBasket'
import usePunchout from '~/composables/punchout/usePunchout'

// Common
import { specialCommerceEnabledCountries } from '@/common/static/commerce'

// Types
import { ElasticFieldsInterface } from '@/types/search/elasticTypes'

// Store
import { useUserStore } from '@/store/user'
import { useCMSGlobalSettingsStore } from '@/store/cms'
import { PRODUCT_CATEGORIES } from '~/common/utils/product/constants'
import { useSearchStore } from '~/store'

export default defineComponent({
  name: 'ProductType',

  components: {
    Applications,
    Reactivity,
    ProductPanel,
    AddToBasketIcon,
    SignalStarIcon,
    Skeleton,
  },

  props: {
    fields: {
      required: true,
      type: Object as () => ElasticFieldsInterface,
    },
    id: {
      required: true,
      type: String,
    },
    index: {
      required: true,
      type: Number,
    },
    score: {
      required: false,
      type: Number,
      default: 0,
    },
  },

  setup(props) {
    const searchStore = useSearchStore()
    const { trackProductClick, trackElasticsearchClickEvent, trackSiteSearchEngagement } = useSearchAnalytics()
    const { getAttributeByCountry } = useCMSGlobalSettingsStore()
    const { infoPanelsOpen, infoPanelOpenIDs } = useSearch()
    const userStore = useUserStore()
    const compareStore = useCompareProductsStore()
    const { isPunchout } = usePunchout()
    const { compareProductExists, comparedProducts } = storeToRefs(compareStore)
    const { selectedCountry } = storeToRefs(userStore)
    const activeChicklet: Ref<string> = ref('')
    const b2dAllowedAddToBasket = getAttributeByCountry('b2dAllowedCountries')

    // ..
    const showSignalStar = computed(
      () =>
        props.fields?.categories?.raw[0] === PRODUCT_CATEGORIES.SIGNALSTAR_MULTIPLEX_IHC_KITS_AND_REAGENTS ||
        props.fields?.categories?.raw[0] === PRODUCT_CATEGORIES.SIGNALSTAR_MULTIPLEX_IHC_KITS_AND_REAGENTS_HTML
    )
    const enableEcommerceIcon = computed(
      () =>
        isPunchout.value ||
        specialCommerceEnabledCountries.includes(selectedCountry.value) ||
        b2dAllowedAddToBasket.value.includes(selectedCountry.value.toLowerCase())
    )

    const toggleInfoPanel = (id: string) => {
      // infoPanelsOpen.value = !infoPanelsOpen.value
      activeChicklet.value = ''
      infoPanelOpenIDs.value = infoPanelOpenIDs.value.filter((el: string) => id !== el)
    }

    const onApplicationInfo = (application: string) => {
      activeChicklet.value = application
      infoPanelsOpen.value = true
      infoPanelOpenIDs.value.push(props.id)
    }

    // when a product is clicked, send data to Elastic App Search
    // and to GTM
    const searchProductClick = (fields: ElasticFieldsInterface, itemName: string) => {
      // Elastic request
      trackElasticsearchClickEvent(fields.productnumber.raw)

      trackSiteSearchEngagement({
        pages_number: searchStore.page?.currentPage,
        query_executed: searchStore.searchInput,
        results_per_page: searchStore.getResults.meta?.page?.size ?? 30,
        view_item_list_id: 'PRP',
        view_item_list_name: 'Product Result Page',
        selected_item_list_name: 'Product Result Page (PRP)',
        selected_result_destination: fields.clickurl?.raw,
        selected_result_text: itemName,
        filters: searchStore.facets.checkedFacets,
        selected_item_index: props.index,
      })

      // GTM
      trackProductClick(fields)
    }

    // Listen to a click on the compare checkbox
    const onClickOnCompare = (fields: ElasticFieldsInterface) => {
      // first chaneg it in the store
      compareStore.toggleCompareProducts(fields.productnumber.raw)
    }

    return {
      showSignalStar,
      enableEcommerceIcon,
      onClickOnCompare,
      comparedProducts,
      compareProductExists,
      infoPanelsOpen,
      toggleInfoPanel,
      searchProductClick,
      onApplicationInfo,
      activeChicklet,
      isInBasket,
      userStore,
      infoPanelOpenIDs,
    }
  },
})
